import React, { useState, useEffect, createContext } from "react"
import useNexmartCartRetailers from "../hooks/useNexmartCartRetailers"

const defaultState = {
  cart: {},
}

const NexmartCartContext = createContext(defaultState)
export default NexmartCartContext

export const NexmartCartContextProvider = ({ children, pageContext }) => {
  const currentLocale = pageContext.locale
  const [showCart, setShowCart] = useState(false)
  const [showNewItemHint, setShowNewItemHint] = useState(false)
  const [showDealerSelection, setShowDealerSelection] = useState(false)
  const [cartItemCount, setCartItemCount] = useState(0)
  const [cartItems, setCartItems] = useState([])
  const [fetchRetailers, setFetchRetailers] = useState(false)
  const [hasRetailers, retailers] = useNexmartCartRetailers(
    cartItems,
    fetchRetailers
  )

  const addItemToCart = (newItem) => {
    const existingItem = cartItems.find(
      (cartItem) => cartItem.externalKey === newItem.externalKey
    )

    if (existingItem) {
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.externalKey === newItem.externalKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      )
    } else {
      setCartItems((prevItems) => [{ ...newItem, quantity: 1 }, ...prevItems])
    }

    setShowNewItemHint(true)
    setCartItemCount((prevCount) => prevCount + 1)
    setShowCart(true)
  }

  const setItemQuantity = (itemNumber, value) => {
    const prevQuantity = cartItems.find(
      (item) => item.externalKey === itemNumber
    ).quantity
    if (prevQuantity > value) {
      setCartItemCount((prevCount) => prevCount - (prevQuantity - value))
    } else {
      setCartItemCount((prevCount) => prevCount + (value - prevQuantity))
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.externalKey === itemNumber
          ? {
              ...item,
              quantity: Math.max(0, value),
            }
          : item
      )
    )
  }

  const decreaseItemQuantity = (itemNumber) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.externalKey === itemNumber
          ? {
              ...item,
              quantity: Math.max(0, item.quantity - 1),
            }
          : item
      )
    )

    setCartItemCount((prevCount) => prevCount - 1)
  }

  const removeItem = (itemExternalKey) => {
    let oldItem = cartItems.find((item) => item.externalKey === itemExternalKey)
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.externalKey !== itemExternalKey)
    )

    setCartItemCount((prevCount) => prevCount - parseInt(oldItem.quantity))
  }

  // reset cart to initial state when closed
  useEffect(() => {
    if (showCart === false) {
      setShowNewItemHint(false)
      setShowDealerSelection(false)
    }
  }, [showCart])

  // 5 seconds to hide the new item hint
  useEffect(() => {
    let newItemTimeout = null
    if (showNewItemHint === true) {
      newItemTimeout = setTimeout(() => {
        setShowNewItemHint(false)
      }, 5000)
    }
    return () => {
      if (newItemTimeout) {
        clearTimeout(newItemTimeout)
      }
    }
  }, [showNewItemHint])

  // init context values from local storage
  useEffect(() => {
    const storedCartItems =
      JSON.parse(sessionStorage.getItem(`cartItems_${currentLocale}`)) || []
    const storedCartItemCount =
      parseInt(sessionStorage.getItem(`cartItemCount_${currentLocale}`)) || 0

    setCartItems(storedCartItems)
    setCartItemCount(storedCartItemCount)
  }, [])

  // for every CartChange
  // - store to localstorage
  // - reset the dealer fetcher
  useEffect(() => {
    sessionStorage.setItem(
      `cartItems_${currentLocale}`,
      JSON.stringify(cartItems)
    )
    sessionStorage.setItem(`cartItemCount_${currentLocale}`, cartItemCount || 0)
    setFetchRetailers(false)
  }, [cartItems, cartItemCount])

  return (
    <NexmartCartContext.Provider
      value={{
        showCart,
        setShowCart,
        cartItemCount,
        setCartItemCount,
        addItemToCart,
        decreaseItemQuantity,
        removeItem,
        cartItems,
        setItemQuantity,
        showNewItemHint,
        showDealerSelection,
        setShowDealerSelection,
        fetchRetailers,
        setFetchRetailers,
        hasRetailers,
        retailers,
      }}
    >
      {children}
    </NexmartCartContext.Provider>
  )
}
